import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { Translate, withTranslate } from 'core/translate';
import { combineClassNames } from 'core/css';
import Modal, { ModalBody, ModalHeader } from '../../components/modal/Modal';
import { CA_POSTAL_REGEX, ZIP_REGEX, servicesPath } from '../../constants';
import { withModelInfo } from '../../common/ModelInfoContext';
import { Button } from '../../../core/controls/button/Button';
import { trackModalLoaded } from '../../factories/analytics/analytics.service';
import fetchPostalCode from '../../api/fetchPostalCode';
import { SvgIcon } from '../../../core/icons';
import { getCookie } from '../../utils/cookieHelpers/cookieHelpers';

const validations = {
  US: {
    maxlength: 5,
    pattern: ZIP_REGEX,
  },
  CA: {
    maxlength: 7,
    pattern: CA_POSTAL_REGEX,
  },
};

const locationCookie = getCookie('GMWP_location');
console.log('location: ', locationCookie);

export const isValidPostalCode = (postalCode, country) => {
  const { pattern } = get(validations, country, {});

  return pattern.test(postalCode);
};

const validate = (postalCode, country) => {
  const url = `${servicesPath}/geo/legacy/en/${country}/${postalCode}`;

  return fetch(url).then((res) => res.json());
};

class PostalCodeModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      hasUseLocationError: false,
      isUpdating: false,
      isDirty: false,
      value: props.initialValue,
    };

    setTimeout(() => {
      trackModalLoaded('location modal');
    }, 100);
  }

  onKeyPress = (event) => {
    this.setState({ hasError: false });

    const enter = 'Enter';
    if (event.key === enter) {
      this.onSubmit(event);
    }
  };

  onFocus = () => {
    const { isDirty } = this.state;
    if (!isDirty) {
      this.setState({
        isDirty: true,
        value: '',
      });
    }
  };

  onChangeValue = (event) => {
    this.setState({
      value: event.target.value,
    });
  };

  updatePostalCode = (postalCode, handleError = null) => {
    const { onUpdate, onClose, modelInfo } = this.props;
    const { country, brand } = modelInfo;
    this.setState(
      {
        isUpdating: true,
      },
      () => {
        validate(postalCode, country, brand).then(
          (data) => {
            onUpdate(postalCode, data, onClose);
          },
          () => {
            if (handleError) {
              handleError();
            }
          }
        );
      }
    );
  };

  onCancel = () => {
    const { onClose, isDefaultPostalCode, fallbackPostalCode } = this.props;

    if (!isDefaultPostalCode) {
      return onClose();
    }

    this.updatePostalCode(fallbackPostalCode, onClose);
  };

  onSubmit = (event) => {
    const { modelInfo } = this.props;
    const { value } = this.state;
    const { country } = modelInfo;

    event.preventDefault();

    const isValidValue = isValidPostalCode(value, country);
    if (!isValidValue) {
      this.setState({
        hasError: true,
      });
      return;
    }

    this.updatePostalCode(value, () =>
      this.setState({
        isUpdating: false,
        hasError: true,
      })
    );
  };

  useLocation = () => {
    const onSuccess = (position) => {
      const { modelInfo } = this.props;
      const { country, language, v3ServicesPath } = modelInfo;

      fetchPostalCode(position.coords, country, language, v3ServicesPath).then(
        (postalCode) => {
          if (!postalCode) {
            return onError();
          }
          this.updatePostalCode(postalCode, () =>
            this.setState({
              isUpdating: false,
              hasUseLocationError: true,
            })
          );
        }
      );
    };

    const onError = () => {
      this.setState({
        hasUseLocationError: true,
      });
    };

    navigator.geolocation.getCurrentPosition(onSuccess, onError);
  };

 

  render() {
    const { translate, modelInfo } = this.props;
    const { country } = modelInfo;
    const { hasError, hasUseLocationError, value } = this.state;

    const header = translate('components.postal.code.modal.title');
    const submitButtonAlt = translate('default.accordion.header.icon.label.v3');
    const buttonLabel = translate('byo.page.general.ok');
    const postalCodePlaceholder = translate('postal.code');

    const { maxlength } = get(validations, country, {});

    const errorMessage = hasError && (
      <div className="postal-code__error">
        <SvgIcon className="postal-code__error--icon" name="warning" />
        <Translate value="postal.code.error.message" />
      </div>
    );

    return (
      <Modal
        isOpen={true}
        fullscreen={false}
        className="postal-code-modal"
        overlayClassName="postal-code__modal-overlay"
        contentLabel={header}
        onRequestClose={this.onCancel}>
        <ModalHeader
          text={header}
          onClose={this.onCancel}
          className="postal-code__modal-header"
          useLargeIcon={true}
        />
        <ModalBody innerClassName="postal-code__modal-body__inner">
          <p className="postal-code__modal-body__inner-text">
            <Translate value="components.postal.code.modal.header" />
          </p>
          {hasUseLocationError && (
            <p className="postal-code__use-location--error">
              <Translate value="postal.code.use.location.error.v3" />
            </p>
          )}
          <div className="postal-code">
            <div className="postal-code__input--container">
              <div className="postal-code__label-text">
              <Translate value="postal.code" />
              </div>
              <div className="postal-code__input-group">
                <input
                  type="search"
                  value={value}
                  onChange={this.onChangeValue}
                  onFocus={this.onFocus}
                  onKeyPress={this.onKeyPress}
                  className={combineClassNames([
                    'postal-code__input',
                    'stat-search-input',
                    hasError ? 'postal-code__input--error' : '',
                  ])}
                  data-dtm={header}
                  maxLength={maxlength}
                  placeholder={postalCodePlaceholder}
                  required
                />
                {/* TODO: use class postal-code__submit-icon--updating to spin icon once we have it to show loading state. */}
                {/* Destructure isUpdating from state to determine when to display the loading state class. Can look at 2.0 for that. */}
                <Button
                  onClick={this.onSubmit}
                  type="submit"
                  className={combineClassNames([
                    'postal-code__button',
                    'stat-button-link',
                    hasError ? 'postal-code__input-button--error' : '',
                  ])}
                  data-dtm={header}
                  id="zipSubmit"
                  aria-label={submitButtonAlt}>
                  <SvgIcon
                    className="postal-code__arrow--icon"
                    name="arrow-right"
                  />
                  <SvgIcon
                    className="postal-code__search--icon"
                    name="icon-search"
                  />
                  <span className="postal-code__button-label">
                    {buttonLabel}
                  </span>
                </Button>
              </div>
              {errorMessage}
            </div>
            <div className="postal-code__divider">
              <div className="postal-code__divider--line" />
              <div className="postal-code__divider--text">
                <Translate value="postal.code.or.v3" />
              </div>
              <div className="postal-code__divider--line" />
            </div>
            <Button
              className="postal-code__use-location--button stat-button-link"
              data-dtm={'location'}
              onClick={this.useLocation}>
              <SvgIcon
                className="postal-code__use-location--icon"
                name="location"
              />
              <Translate value="postal.code.use.location.v3" />
            </Button>
          </div>
        </ModalBody>
      </Modal>
    );
  }
}

PostalCodeModal.defaultProps = {
  initialValue: '',
};

PostalCodeModal.propTypes = {
  initialValue: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default withModelInfo(withTranslate(PostalCodeModal));
